const finder=document.querySelector('coffee-finder');
const [{loadCatalog, rankCoffees, eligibleVariants, preferenceAvailable, matchingDiagnostics}]=await Promise.all([import(finder.dataset.modelUrl),import(finder.dataset.selectUrl)]);

let createQuizMeasurement;
// Load independently: a slow or blocked analytics asset must never hold the quiz.
import(finder.dataset.measurementUrl).then(module=>{createQuizMeasurement=module.createQuizMeasurement;if(finder.initialized&&!finder.measurement)finder.measurement=createQuizMeasurement(finder);}).catch(()=>{});

class CoffeeFinder extends HTMLElement {
  connectedCallback() {
    if(this.initialized) return;
    this.initialized = true;
    this.copy = JSON.parse(this.querySelector('[data-copy]').textContent);
    this.addEventListener('coffee-finder-unavailable',event=>this.explainUnavailable(event.detail.label,event.detail.control,event.detail.field,event.detail.value));
    this.addEventListener('click',event=>{
      const choice=event.target.closest('.coffee-finder__choice'),input=choice?.querySelector('input');
      if(input?.getAttribute('aria-disabled')==='true'){event.preventDefault();event.stopPropagation();this.explainUnavailable(choice.querySelector('strong').textContent,input);}
    },true);
    this.addEventListener('keydown',event=>{
      const input=event.target;
      if(!input.matches('input[type=radio]'))return;
      if([' ','Enter'].includes(event.key)&&input.getAttribute('aria-disabled')==='true'){event.preventDefault();this.explainUnavailable(input.closest('label').querySelector('strong').textContent,input);return;}
      if(['ArrowDown','ArrowRight','ArrowUp','ArrowLeft'].includes(event.key)){
        event.preventDefault();const radios=[...this.form.querySelectorAll(`input[name="${input.name}"]`)];const direction=['ArrowUp','ArrowLeft'].includes(event.key)?-1:1;
        const next=radios[(radios.indexOf(input)+direction+radios.length)%radios.length];next.focus();
        if(next.getAttribute('aria-disabled')!=='true'){next.checked=true;next.dispatchEvent(new Event('change',{bubbles:true}));}
      }
    });
    this.form = this.querySelector('.coffee-finder__form');
    this.recovery=this.querySelector('[data-recovery]');
    this.recoveryForm=this.querySelector('[data-recovery-form]');
    this.recoveryForm.addEventListener('change',event=>{this.measurement?.recoveryChange(event.target.name,event.target.value);this.reviewRecovery();});
    this.recoveryForm.addEventListener('submit',event=>{
      event.preventDefault();
      if(this.busy || this.querySelector('[data-update-matches]').disabled) return;
      for(const [name,value] of Object.entries(this.recoveryAnswers())) {
        const radio=this.form.querySelector(`input[name="${name}"][value="${value}"]`);
        if(radio) radio.checked=true;
        else {const select=this.form.querySelector(`select[name="${name}"]`);select.value=value??'';select.dispatchEvent(new Event('change',{bubbles:true}));}
      }
      this.measurement?.submit('update',this.answers());
      this.showResults(true);
    });
    this.form.addEventListener('invalid',event=>{
      const disclosure=event.target.closest('details');
      if(disclosure) disclosure.open=true;
    },true);
    this.results = this.querySelector('.coffee-finder__results');
    this.matchContainer = this.querySelector('[data-matches]');
    this.form.addEventListener('change',()=>{
      this.savePosition();
      if(this.availabilityCheckedAt && Date.now()-this.availabilityCheckedAt<30000)this.updateOptionAvailability();
    });
    this.matchContainer.addEventListener('change',event=>{this.savePosition();const card=event.target.closest('.coffee-finder__match');if(card)this.measurement?.variant(card.dataset.productId,event.target.value);});
    this.browse = this.querySelector('.coffee-finder__browse');
    this.step = 0; this.generation = 0; this.resultIndex = 0; this.resultChoices = new Map(); this.snapshotCheckedAt=0;
    this.carousel = this.querySelector('[data-carousel]');
    this.range = this.querySelector('[data-match-range]');
    this.previous = this.querySelector('[data-previous-match]');
    this.next = this.querySelector('[data-next-match]');
    this.wideResults = matchMedia('(min-width:700px)');
    this.onResize = () => {
      if(this.results.hidden || this.busy) return;
      if(!this.snapshotCheckedAt || Date.now()-this.snapshotCheckedAt>=30000) {this.showResults(false);return;}
      const choices=this.rememberChoices();
      this.pageSize=this.wideResults.matches?2:1;
      this.resultIndex=Math.floor(this.resultIndex/this.pageSize)*this.pageSize;
      this.renderMatches(this.rankedMatches,choices);
    };
    this.wideResults.addEventListener('change', this.onResize);
    this.previous.addEventListener('click',()=>this.moveResults(-1));
    this.next.addEventListener('click',()=>this.moveResults(1));
    this.setupResultGestures();
    this.form.addEventListener('submit', event => {
      event.preventDefault();
      if(this.busy) return;
      const active = this.querySelector(`[data-step="${this.step}"]`);
      const invalid = [...active.querySelectorAll('input,select')].find(el=>!el.checkValidity());
      if(invalid) { invalid.reportValidity(); return; }
      this.measurement?.stepComplete(this.step,this.answers());
      if(this.step===2)this.measurement?.submit('submit',this.answers());
      if(this.step < 2) this.showStep(this.step + 1);
      else this.showResults();
    });
    this.querySelector('[data-back]').addEventListener('click',()=>this.showStep(this.step-1));
    this.querySelector('[data-edit]').addEventListener('click',()=>{this.measurement?.edit();this.cancel();this.showStep(0);});
    this.querySelector('[data-retry]').addEventListener('click',()=>{this.measurement?.submit('retry',this.answers());this.showResults(false);});
    this.onVisibility = () => {
      if(this.results.hidden){if(!document.hidden)this.refreshAvailability();return;}
      if(this.results.hidden || this.openingProduct) return;
      clearTimeout(this.returnRefresh);
      if(document.hidden) { this.cancel(); }
      else if(!this.refreshing && (this.busy || !this.snapshotCheckedAt || Date.now()-this.snapshotCheckedAt>=30000)) this.showResults(false);
      else if(!this.refreshing) {
        // Resume the freshness deadline after a hidden tab skipped its interval.
        this.returnRefresh=setTimeout(()=>{if(!document.hidden && !this.results.hidden && !this.busy && !this.refreshing) this.showResults(false);},Math.max(0,25000-(Date.now()-this.snapshotCheckedAt)));
      }
    };
    document.addEventListener('visibilitychange', this.onVisibility);
    // Refresh before the 30-second freshness limit so unchanged results need not disappear.
    this.interval = setInterval(()=>{if(document.hidden)return;if(this.results.hidden)this.refreshAvailability();else if(!this.busy && !this.refreshing)this.showResults(false);},25000);
    try {this.measurement=createQuizMeasurement?.(this);} catch {}
    this.showStep(0, false);
    // Currency is read from the same market-aware feed, not guessed from language.
    this.currency = this.dataset.currency; this.country = this.dataset.country;
    this.onPageHide=()=>this.savePosition();
    window.addEventListener('pagehide',this.onPageHide);
    this.querySelector('[data-next]').disabled=false;
    this.restorePosition();
    if(!this.busy)this.refreshAvailability();
  }
  disconnectedCallback() { this.gestureController?.abort();this.measurement?.disconnect();this.cancel(); this.budgetController?.abort();this.availabilityController?.abort();clearTimeout(this.availabilityExpiry);this.unavailableDialog?.remove(); clearInterval(this.interval); clearTimeout(this.returnRefresh); document.removeEventListener('visibilitychange',this.onVisibility); window.removeEventListener('pagehide',this.onPageHide); this.wideResults.removeEventListener('change',this.onResize); }
  explainUnavailable(label,control,field=control?.name,value=control?.value) {
    this.measurement?.unavailable(field,value);
    if(!this.unavailableDialog){
      const dialog=this.unavailableDialog=this.element('dialog',null,'coffee-finder__unavailable-dialog');
      const title=this.element('h2',this.copy.unavailable_title),message=this.element('p');title.id='CoffeeUnavailableTitle-'+this.dataset.locale;message.id=title.id+'-message';
      dialog.setAttribute('aria-labelledby',title.id);dialog.setAttribute('aria-describedby',message.id);
      const close=this.element('button',this.copy.unavailable_close,'coffee-finder__primary');close.type='button';close.addEventListener('click',()=>dialog.close());
      dialog.append(title,message,close);dialog.addEventListener('click',event=>{if(event.target===dialog){const r=dialog.getBoundingClientRect();if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom)dialog.close();}});
      dialog.addEventListener('close',()=>this.unavailableControl?.focus({preventScroll:true}));this.append(dialog);
    }
    this.unavailableControl=control;
    const fresh=this.availabilityCheckedAt && Date.now()-this.availabilityCheckedAt<30000;
    this.unavailableDialog.querySelector('p').textContent=fresh?this.copy.unavailable_detail.replace('{option}',label):this.copy.error;
    if(!fresh)this.refreshAvailability();
    if(!this.unavailableDialog.open)this.unavailableDialog.showModal();
  }
  applyAvailability(catalog,requestedAt=Date.now()) {
    if(requestedAt<(this.availabilityRequestAt||0))return;
    this.availabilityRequestAt=requestedAt;this.availabilityCatalog=catalog;this.availabilityCheckedAt=Date.now();
    clearTimeout(this.availabilityExpiry);this.availabilityExpiry=setTimeout(()=>this.clearAvailability(),30000);
    this.updateOptionAvailability();
  }
  updateOptionAvailability() {
    const catalog=this.availabilityCatalog;if(!catalog)return;
    const answers=this.answers();
    for(const input of this.form.querySelectorAll('input[type=radio]')){
      const unavailable=!preferenceAvailable(catalog,input.name,input.value,answers);input.setAttribute('aria-disabled',String(unavailable));
      const choice=input.closest('label');choice.toggleAttribute('data-unavailable',unavailable);
      let badge=choice.querySelector('[data-option-unavailable]');if(unavailable&&!badge){badge=this.element('small',this.copy.unavailable,'coffee-finder__option-unavailable');badge.setAttribute('data-option-unavailable','');choice.lastElementChild.append(badge);}else if(!unavailable)badge?.remove();
    }
    for(const select of this.querySelectorAll('.coffee-finder__form select,.coffee-finder__recovery-form select')){
      const current=select.closest('.coffee-finder__recovery-form')?this.recoveryAnswers():answers;
      for(const option of select.options){const unavailable=!preferenceAvailable(catalog,select.name,option.value,current);if(unavailable){option.disabled=true;option.dataset.cfUnavailable=this.copy.unavailable;}else if(option.dataset.cfUnavailable){option.disabled=false;delete option.dataset.cfUnavailable;}}
      select.closest('coffee-finder-select')?.sync?.();
    }
  }
  clearAvailability(requestedAt=Infinity) {
    if(requestedAt<(this.availabilityRequestAt||0))return;
    if(Number.isFinite(requestedAt))this.availabilityRequestAt=requestedAt;
    this.availabilityCheckedAt=0;
    this.form.querySelectorAll('input[type=radio]').forEach(input=>{input.removeAttribute('aria-disabled');input.closest('label').removeAttribute('data-unavailable');});
    this.querySelectorAll('[data-option-unavailable]').forEach(badge=>badge.remove());
    this.querySelectorAll('option[data-cf-unavailable]').forEach(option=>{option.disabled=false;delete option.dataset.cfUnavailable;});
    this.querySelectorAll('coffee-finder-select').forEach(picker=>picker.sync?.());
  }
  async refreshAvailability() {
    if(this.availabilityLoading || !this.isConnected)return;
    this.availabilityLoading=true;const requestedAt=Date.now(),controller=this.availabilityController=new AbortController(),timer=setTimeout(()=>controller.abort(),15000);
    try {const catalog=await loadCatalog(this.dataset.endpoint,{signal:controller.signal});if(!this.isConnected)return;if(catalog.currency!==this.currency||catalog.country!==this.country)throw new Error('Market changed');this.applyAvailability(catalog,requestedAt);}
    catch {if(this.isConnected)this.clearAvailability(requestedAt);}
    finally {clearTimeout(timer);this.availabilityLoading=false;}
  }
  scrollContainer() { return matchMedia('(min-width:990px)').matches ? document.querySelector('.page-wrapper') || document.scrollingElement : document.scrollingElement; }
  savePosition() {
    // History belongs to this quiz entry, unlike shared storage across product tabs.
    // Save choices only: inventory must always be fetched again after a real reload.
    try {
      const position={version:1,at:Date.now(),path:location.pathname,locale:this.dataset.locale,currency:this.currency,country:this.country,
        answers:this.answers(),step:this.step,results:!this.results.hidden,index:this.resultIndex,anchor:this.anchorId,
        choices:[...this.rememberChoices()],scroll:this.scrollContainer().scrollTop};
      history.replaceState({...history.state,scrollTop:position.scroll,coffeeFinder:position},'');
    } catch {} // Private browsing or a full history store must not break View.
  }
  async restorePosition() {
    const saved=history.state?.coffeeFinder;
    if(!saved || saved.version!==1 || saved.path!==location.pathname || saved.locale!==this.dataset.locale || saved.currency!==this.currency || saved.country!==this.country || !Number.isFinite(saved.at) || !saved.answers) return;
    history.scrollRestoration='manual';
    for(const [name,value] of Object.entries(saved.answers)) {
      if(!['flavor','method','caffeine','budget','process','acidity','body'].includes(name)) continue;
      const controls=[...this.form.querySelectorAll(`[name="${name}"]`)];
      const radio=controls.find(el=>el.type==='radio' && el.value===value);
      if(radio) radio.checked=true;
      else if(controls[0]?.tagName==='SELECT') {
        const select=controls[0],text=value==null?'':String(value);
        if(name==='budget' && Number(value)>0 && Number.isFinite(value) && ![...select.options].some(o=>o.value===text)) {
          const option=this.element('option',this.copy.budget_up_to.replace('{price}',new Intl.NumberFormat(this.dataset.locale,{style:'currency',currency:this.currency}).format(value)));option.value=text;select.append(option);
          const picker=select.closest('coffee-finder-select'),replacement=picker.cloneNode(false),native=select.cloneNode(true);
          native.hidden=false;native.removeAttribute('tabindex');native.value=text;
          this.form.querySelector('[data-budget-field]>label').htmlFor=native.id;
          replacement.append(native,this.querySelector('[data-select-caret]').content.cloneNode(true));picker.replaceWith(replacement);continue;
        }
        if([...select.options].some(o=>o.value===text)) select.value=text;
        select.closest('coffee-finder-select')?.sync?.();
      }
    }
    this.resultChoices=new Map((Array.isArray(saved.choices)?saved.choices:[]).filter(pair=>Array.isArray(pair) && pair.length===2 && /^\d+$/.test(String(pair[0])) && /^\d+$/.test(String(pair[1]))).slice(0,200));
    this.showStep(Number.isInteger(saved.step)?saved.step:0,false);
    if(saved.results) await this.showResults(false,{anchor:saved.anchor,index:Number.isInteger(saved.index)&&saved.index>=0?saved.index:0});
    if(Number.isFinite(saved.scroll) && saved.scroll>=0) requestAnimationFrame(()=>this.scrollContainer().scrollTo({top:saved.scroll,behavior:'instant'}));
  }
  async loadBudgetChoices() {
    if(this.budgetLoading) return;
    this.budgetLoading=true;
    const requestedAt=Date.now(),controller=this.budgetController=new AbortController(),timer=setTimeout(()=>controller.abort(),15000);
    const status=this.querySelector('[data-budget-status]');status.textContent=this.copy.budget_loading;
    try {
      const catalog=await loadCatalog(this.dataset.endpoint,{signal:controller.signal});
      if(!this.isConnected) return;
      if(catalog.currency!==this.currency || catalog.country!==this.country) throw new Error('Market changed');
      this.applyAvailability(catalog,requestedAt);
      const prices=rankCoffees(catalog,{flavor:'any',method:'any',caffeine:'any'}).flatMap(match=>match.variants.map(v=>v.price));
      const max=Math.max(...prices);
      const ceilings=[...new Set(prices.map(price=>Math.ceil(price/500)*500))].filter(price=>price<max).sort((a,b)=>a-b);
      const current=this.form.querySelector('[name=budget]'),selected=current.value;
      // Keep a shopper's hard limit even if the current price bands changed.
      if(selected && !ceilings.includes(Number(selected)*100)) ceilings.push(Number(selected)*100);
      ceilings.sort((a,b)=>a-b);
      const picker=this.element('coffee-finder-select',null,'coffee-finder__select'),select=this.element('select');
      select.id=current.id;select.name='budget';
      const any=this.element('option',this.copy.budget_any);any.value='';select.append(any);
      const money=new Intl.NumberFormat(this.dataset.locale,{style:'currency',currency:this.currency});
      for(const price of ceilings) {const option=this.element('option',this.copy.budget_up_to.replace('{price}',money.format(price/100)));option.value=String(price/100);select.append(option);}
      select.value=selected;picker.append(select,this.querySelector('[data-select-caret]').content.cloneNode(true));
      this.querySelector('[data-budget-field]>label').htmlFor=select.id;
      this.querySelector('[data-budget-picker]').replaceChildren(picker);
      this.syncFormPickers();
      this.applyAvailability(catalog,requestedAt);
      status.textContent='';
    } catch(error) {
      this.measurement?.error('budget_unavailable','budget');
      if(this.isConnected) {
        status.textContent=this.copy.budget_error+' ';
        const retry=this.element('button',this.copy.retry);retry.type='button';retry.addEventListener('click',()=>this.loadBudgetChoices());status.append(retry);
      }
    } finally {clearTimeout(timer);this.budgetLoading=false;}
  }
  cancel() { this.controller?.abort(); this.generation++; this.refreshing=false; }
  syncFormPickers() {
    this.form.querySelectorAll('coffee-finder-select').forEach(picker=>{
      const button=picker.querySelector('button');
      if(button) button.disabled=picker.querySelector('select').matches(':disabled');
    });
  }
  showStep(step, focus = true) {
    this.cancel();this.busy=false;
    this.form.removeAttribute('aria-busy');
    this.form.querySelector(':scope > [role="status"]').classList.remove('visually-hidden');
    this.querySelector('[data-next]').disabled=false;
    this.step = Math.max(0,Math.min(2,step));
    this.querySelector('.coffee-finder__hero').hidden=false;
    this.recovery.hidden=true;this.classList.remove('coffee-finder--empty');
    this.form.hidden=false; this.results.hidden=true; this.browse.hidden=true; this.classList.remove('coffee-finder--results');
    this.querySelectorAll('[data-step]').forEach((el,i)=>{
      el.hidden=i!==this.step;
      // Inactive required radios must not stop the native form submission.
      el.disabled=i!==this.step;
    });
    this.syncFormPickers();
    this.updateProgress(this.step);
    if(this.step===2) this.loadBudgetChoices();
    this.querySelector('[data-back]').hidden=this.step===0;
    this.querySelector('[data-next]').firstChild.textContent=this.copy[this.step===2?'show_matches':'continue']+' ';
    this.form.querySelector(':scope > [role="status"]').textContent='';
    if(focus) {this.querySelector(`[data-step="${this.step}"] legend`).focus();this.savePosition();this.measurement?.step(this.step);}
  }
  updateProgress(step) {
    this.querySelectorAll('[data-progress]').forEach((el,i)=>{
      if(i===step) el.setAttribute('aria-current','step'); else el.removeAttribute('aria-current');
    });
  }
  answers() {
    // Disabled steps retain their values but are not present in FormData.
    const val=name=>{
      const field=this.form.querySelector(`[name="${name}"]`);
      return field?.type==='radio'?this.form.querySelector(`[name="${name}"]:checked`)?.value||'any':field?.value;
    };
    return Object.fromEntries(['flavor','method','caffeine','budget','process','acidity','body'].map(key=>[key,key==='budget'?Number(val(key))||null:val(key)]));
  }
  recoveryAnswers() {
    return Object.fromEntries([...this.recoveryForm.querySelectorAll('select')].map(select=>[select.name,select.name==='budget'?Number(select.value)||null:select.value]));
  }
  setRecovery(catalog, matches) {
    this.recoveryCatalog=catalog;
    const empty=!matches.length;
    const wasEmpty=!this.recovery.hidden;
    this.recovery.hidden=!empty;
    this.classList.toggle('coffee-finder--empty',empty);
    this.results.setAttribute('aria-label',this.copy[empty?'empty_title':'results']);
    this.results.querySelector(':scope > h2').hidden=empty;
    this.querySelector('[data-results-help]').hidden=empty;
    this.querySelector('[data-edit]').hidden=empty;
    if(!empty) return;
    if(!wasEmpty) {
      const answers=this.answers();
      for(const select of this.recoveryForm.querySelectorAll('select')) {
        if(select.name==='budget') {
          const source=this.form.querySelector('[name=budget]');
          const picker=select.closest('coffee-finder-select'), replacement=picker.cloneNode(false);
          replacement.className=picker.className;
          const native=select.cloneNode(false);native.append(...[...source.options].map(option=>option.cloneNode(true)));native.hidden=false;native.removeAttribute('tabindex');
          this.recoveryForm.querySelector('label[for="'+native.id+'-control"]').htmlFor=native.id;
          replacement.append(native,this.querySelector('[data-select-caret]').content.cloneNode(true));picker.replaceWith(replacement);
          native.value=answers.budget??'';replacement.sync?.();
        } else {select.value=answers[select.name]??'any';select.closest('coffee-finder-select').sync?.();}
      }
    }
    const available=rankCoffees(catalog,{flavor:'any',method:'any',caffeine:'any'});
    const caffeine=this.answers().caffeine;
    const unavailable=caffeine!=='any' && !available.some(match=>match.product.profile.caffeine===caffeine);
    this.querySelector('[data-empty-message]').textContent=this.copy[!available.length?'empty_inventory':unavailable?'empty_'+caffeine:'empty_combination'];
    this.applyAvailability(catalog);
    this.reviewRecovery();
  }
  reviewRecovery() {
    if(!this.recoveryCatalog || this.recovery.hidden) return;
    if(this.availabilityCheckedAt && Date.now()-this.availabilityCheckedAt<30000)this.updateOptionAvailability();
    const answers=this.recoveryAnswers();
    const available=rankCoffees(this.recoveryCatalog,{flavor:'any',method:'any',caffeine:answers.caffeine});
    this.querySelector('[data-caffeine-unavailable]').hidden=!!available.length || answers.caffeine==='any';
    const original=this.answers();
    const changed=Object.keys(answers).some(key=>answers[key]!==original[key]);
    const count=rankCoffees(this.recoveryCatalog,answers).length;
    this.querySelector('[data-update-matches]').disabled=this.busy || !changed || !count;
    this.querySelector('[data-recovery-status]').textContent=this.busy?this.copy.loading:this.recoveryError?this.copy.error:(!changed?'':this.copy[count?'recovery_ready':'recovery_none']);
  }
  async freshCatalog() {
    clearTimeout(this.returnRefresh);
    this.cancel();
    this.controller=new AbortController(); const requestedAt=Date.now(),generation=this.generation, controller=this.controller;
    const timer=setTimeout(()=>controller.abort(),15000);
    try {
      const catalog=await loadCatalog(this.dataset.endpoint,{signal:this.controller.signal});
      if(generation!==this.generation) throw new DOMException('Superseded','AbortError');
      if(catalog.currency!==this.currency || catalog.country!==this.country) throw new Error('Market changed; reload required');
      this.currency=catalog.currency;
      this.applyAvailability(catalog,requestedAt);
      return {catalog,generation};
    } catch(error) { error.cfGeneration=generation; throw error; } finally {clearTimeout(timer);}
  }
  clearMatches() {
    this.matchContainer.replaceChildren();
    this.snapshotCheckedAt=0;
    this.carousel.hidden=true;
  }
  rememberChoices() {
    for(const card of this.matchContainer.children) this.resultChoices.set(card.dataset.productId,card.querySelector('select').value);
    return this.resultChoices;
  }
  setupResultGestures() {
    const controller=this.gestureController=new AbortController(),signal=controller.signal;
    const active=()=>!this.results.hidden&&!document.hidden;
    const ready=()=>active()&&!this.carousel.hidden&&this.rankedMatches?.length>this.pageSize;
    const control=target=>target.closest?.('a,button,select,input,textarea,[role=combobox],[role=listbox]');
    // One page per direction in a wheel burst. A deliberate reversal must rearm
    // immediately; small opposite-direction momentum noise must not unlock it.
    const resetWheel=at=>({at,direction:0,total:0,reverse:0,moved:false,last:0,peak:0,low:Infinity,decay:0,movedAt:0,paused:null});
    let wheel=resetWheel(0);
    document.addEventListener('wheel',event=>{
      if(!active()||event.defaultPrevented||event.ctrlKey||event.metaKey||event.altKey)return;
      const x=Math.abs(event.deltaX),y=Math.abs(event.deltaY);
      if(!x||x<=y){if(y>=8)wheel=resetWheel(0);return;}
      const now=performance.now();
      const gap=now-wheel.at;
      if(gap>320)wheel=resetWheel(now);
      wheel.at=now;
      // Cancel even a tiny horizontal onset, at the document capture phase. A
      // fling can leave a card's bounds; it still must not become browser Back.
      // Some browsers expose noncancelable tail events after the first packet.
      if(event.cancelable)event.preventDefault();
      if(this.carousel.hidden||!this.carousel.contains(event.target)||control(event.target)){wheel.moved=true;wheel.paused=null;return;}
      const unit=event.deltaMode===1?16:event.deltaMode===2?this.carousel.clientWidth:1;
      const distance=x*unit,direction=Math.sign(event.deltaX);
      // WheelEvent has no portable finger-lift/momentum phase. Infer a new
      // same-direction stroke from a pause plus renewed strength, or a clear
      // acceleration after a decaying tail. A falling tail alone stays locked.
      const renewedImpulse=wheel.decay>=2&&now-wheel.movedAt>=70&&distance>=wheel.low*3;
      if(wheel.moved&&direction===wheel.direction){
        if(gap>=90){
          if(distance>=wheel.last)wheel=resetWheel(now);
          else wheel.paused={total:distance,last:distance};
        }else if(wheel.paused){
          // A weak first packet after a pause can still begin a gentle stroke.
          // Confirm non-falling input before accumulating it; discard decay.
          if(distance>=wheel.paused.last)wheel={...resetWheel(now),direction,total:wheel.paused.total};
          else wheel.paused=null;
        }
        if(wheel.moved&&renewedImpulse)wheel=resetWheel(now);
      }
      if(direction!==wheel.direction)wheel.paused=null;
      if(wheel.moved&&wheel.direction&&direction!==wheel.direction){
        wheel.reverse+=distance;
        if(wheel.reverse<56)return;
        wheel={...resetWheel(now),direction,total:wheel.reverse};
      }else{
        if(wheel.direction!==direction){wheel.direction=direction;wheel.total=0;}
        wheel.reverse=0;wheel.total+=distance;
      }
      wheel.peak=Math.max(wheel.peak,distance);
      if(wheel.moved&&distance<wheel.last&&distance<=wheel.peak*.5)wheel.decay++;
      if(wheel.moved&&wheel.decay>=2)wheel.low=Math.min(wheel.low,distance);
      wheel.last=distance;
      if(!wheel.moved&&Math.abs(wheel.total)>=56){
        wheel.moved=true;wheel.movedAt=now;this.moveResults(direction,{focus:false});
      }
    },{capture:true,passive:false,signal});
    let touch=null,suppressClickUntil=0;
    this.matchContainer.addEventListener('pointerdown',event=>{
      if(!event.isPrimary){touch=null;return;}
      if(!ready()||event.pointerType==='mouse'||control(event.target))return;
      touch={id:event.pointerId,x:event.clientX,y:event.clientY,dx:0,horizontal:false};
      this.matchContainer.setPointerCapture(event.pointerId);
    },{signal});
    this.matchContainer.addEventListener('pointermove',event=>{
      if(!touch||touch.id!==event.pointerId)return;
      const dx=event.clientX-touch.x,dy=event.clientY-touch.y;
      if(!touch.horizontal&&Math.abs(dy)>8&&Math.abs(dy)>=Math.abs(dx)){touch=null;return;}
      if(Math.abs(dx)>8&&Math.abs(dx)>Math.abs(dy)*1.25)touch.horizontal=true;
      touch.dx=dx;
      if(touch.horizontal&&event.cancelable)event.preventDefault();
    },{passive:false,signal});
    this.matchContainer.addEventListener('pointerup',event=>{
      if(!touch||touch.id!==event.pointerId)return;
      const gesture=touch;touch=null;
      if(gesture.horizontal&&Math.abs(gesture.dx)>=48){
        suppressClickUntil=performance.now()+500;
        this.moveResults(gesture.dx<0?1:-1,{focus:false});
      }
    },{signal});
    this.matchContainer.addEventListener('pointercancel',()=>{touch=null;},{signal});
    this.matchContainer.addEventListener('click',event=>{
      if(performance.now()<suppressClickUntil){event.preventDefault();event.stopPropagation();}
    },{capture:true,signal});
  }
  async moveResults(direction,{focus=true}={}) {
    if(this.busy || this.results.hidden || document.hidden) return;
    const index=this.resultIndex+direction*this.pageSize;
    if(index<0 || index>=this.rankedMatches.length) return;
    // Browse the already-ranked snapshot immediately; an expired snapshot must be refreshed first.
    if(Date.now()-this.snapshotCheckedAt>=30000) {
      await this.showResults(false, {anchor:this.rankedMatches[index].product.id, index, navigation:true,focusNavigation:focus});return;
    }
    const control=direction<0?this.previous:this.next,choices=this.rememberChoices();
    this.resultIndex=index;
    this.measurement?.browse(index);
    this.renderMatches(this.rankedMatches,choices);
    this.results.querySelector(':scope > [role="status"]').textContent=this.range.textContent;
    if(focus)(control.disabled ? (control===this.next?this.previous:this.next) : control).focus({preventScroll:true});
    this.savePosition();
  }
  async showResults(focus = true, requested = {}) {
    const entering=this.results.hidden;
    const recovering=!this.recovery.hidden;
    const background=!this.busy && !focus && !requested.navigation && this.snapshotCheckedAt>0 && Date.now()-this.snapshotCheckedAt<30000 && (this.matchContainer.childElementCount>0 || !this.recovery.hidden);
    // Keep the current product/size on a background refresh; never return to page one silently.
    const active=document.activeElement;
    const activeCard=active?.closest('.coffee-finder__match');
    const savedFocus=activeCard && {id:activeCard.dataset.productId, picker:active.matches('[role=combobox]')};
    const choices=this.rememberChoices();
    const anchor=requested.anchor ?? (!focus ? this.anchorId : null);
    const oldIndex=requested.index ?? (!focus ? this.resultIndex : 0);
    if(entering) {
      this.form.querySelectorAll('coffee-finder-select').forEach(picker=>picker.close?.());
      this.querySelector('[data-step="2"]').disabled=true;this.syncFormPickers();
      this.querySelector('[data-next]').disabled=true;
      this.form.setAttribute('aria-busy','true');
      const announcement=this.form.querySelector(':scope > [role="status"]');
      announcement.classList.add('visually-hidden');announcement.textContent=this.copy.loading;
    }
    this.browse.hidden=true;
    const status=this.results.querySelector(':scope > [role="status"]');
    if(!background) {this.busy=true;this.clearMatches();status.textContent=this.copy.loading;}
    this.querySelector('[data-retry]').hidden=true;
    const request=this.freshCatalog(), refreshGeneration=this.generation;
    this.refreshing=true;if(!this.recovery.hidden) {this.recoveryForm.setAttribute('aria-busy','true');this.reviewRecovery();}
    // A slow background request must not extend the old snapshot's lifetime.
    const expiry=background ? setTimeout(()=>{
      if(refreshGeneration===this.generation) {this.busy=true;this.clearMatches();status.textContent=this.copy.loading;}
    },Math.max(0,30000-(Date.now()-this.snapshotCheckedAt))) : null;
    try {
      const {catalog}=await request;
      const matches=rankCoffees(catalog,this.answers());
      // The shopper may have paged or picked a size while the quiet check ran.
      const currentAnchor=background ? this.anchorId : anchor;
      const currentIndex=background ? this.resultIndex : oldIndex;
      const currentChoices=background && this.matchContainer.childElementCount ? this.rememberChoices() : choices;
      const unchanged=background && this.matchContainer.childElementCount>0 && JSON.stringify(matches)===JSON.stringify(this.rankedMatches);
      const found=currentAnchor ? matches.findIndex(match=>match.product.id===currentAnchor) : -1;
      this.pageSize=this.wideResults.matches?2:1;
      const index=found>=0 ? found : Math.min(currentIndex,Math.max(0,matches.length-1));
      this.resultIndex=Math.floor(index/this.pageSize)*this.pageSize;
      this.rankedMatches=matches;
      this.snapshotCheckedAt=Date.now();
      if(!unchanged) this.renderMatches(matches, currentChoices);
      this.recoveryError=false;this.setRecovery(catalog,matches);
      const changed=currentAnchor && found<0;
      status.textContent=matches.length ? (changed?this.copy.stock_changed:requested.navigation?this.range.textContent:'') : '';
      if(entering) {
        // Decode only the cards about to be shown, without an unbounded image wait.
        const images=[...this.matchContainer.querySelectorAll('img')];
        images.forEach(image=>{image.loading='eager';});
        let imageTimer;
        await Promise.race([Promise.allSettled(images.map(image=>image.decode())),new Promise(resolve=>{imageTimer=setTimeout(resolve,2000);})]);
        clearTimeout(imageTimer);
        if(refreshGeneration!==this.generation) return;
        this.form.hidden=true;this.results.hidden=false;
        this.classList.add('coffee-finder--results');
        this.querySelector('.coffee-finder__hero').hidden=true;
        this.updateProgress(3);
        if(focus) {
          this.results.querySelector(!this.recovery.hidden?'[data-recovery] h2':':scope > h2').focus({preventScroll:true});
          this.querySelector('.coffee-finder__panel').scrollIntoView({block:'start',behavior:'instant'});
        }
      }
      if(recovering && focus && !entering) {
        this.results.querySelector(!this.recovery.hidden?'[data-recovery] h2':':scope > h2').focus({preventScroll:true});
        this.querySelector('.coffee-finder__panel').scrollIntoView({block:'start',behavior:'instant'});
      }
      if(requested.navigation) {
        // Keep keyboard focus on the same control, or its opposite at an endpoint.
        const control=active===this.previous?this.previous:this.next;
        this.measurement?.browse(this.resultIndex);
        if(requested.focusNavigation!==false){
          if(!matches.length || matches.length<=this.pageSize) this.results.querySelector(!this.recovery.hidden?'[data-recovery] h2':':scope > h2').focus();
          else (control.disabled ? (control===this.next?this.previous:this.next) : control).focus();
        }
      } else if(savedFocus && !unchanged) {
        const card=[...this.matchContainer.children].find(card=>card.dataset.productId===savedFocus.id);
        const target=card?.querySelector(savedFocus.picker?'[role=combobox]':'.coffee-finder__primary');
        if(target) target.focus({preventScroll:true});
        else {this.results.querySelector(!this.recovery.hidden?'[data-recovery] h2':':scope > h2').focus({preventScroll:true});status.textContent=this.copy.stock_changed;}
      }
      if(focus) this.savePosition();
      this.measurement?.results(matches.length,this.answers(),matchingDiagnostics(catalog,this.answers()));
    } catch(error) {
      if(error.cfGeneration !== undefined && error.cfGeneration !== this.generation) return;
      if(error.name==='AbortError' && this.controller?.signal.aborted && document.hidden) return;
      this.measurement?.error('catalog_unavailable',entering?'submit':'refresh');
      this.clearMatches(); status.textContent=this.copy.error;
      if(entering) {
        const announcement=this.form.querySelector(':scope > [role="status"]');
        announcement.classList.remove('visually-hidden');announcement.textContent=this.copy.error;
      }
      this.querySelector('[data-retry]').hidden=false;this.browse.hidden=true;
      if(!this.recovery.hidden) {this.recoveryError=true;this.querySelector('[data-recovery-status]').textContent=this.copy.error;}
    } finally { clearTimeout(expiry);if(refreshGeneration===this.generation) {
      this.busy=false;this.refreshing=false;this.recoveryForm.removeAttribute('aria-busy');this.reviewRecovery();
      if(entering) {
        this.querySelector('[data-step="2"]').disabled=false;this.syncFormPickers();
        this.form.removeAttribute('aria-busy');this.querySelector('[data-next]').disabled=false;
        if(!this.results.hidden) this.form.querySelector(':scope > [role="status"]').textContent='';
      }
    } }
  }
  renderMatches(matches, choices=new Map()) {
    this.matchContainer.replaceChildren();
    this.browse.hidden=true;
    this.carousel.hidden=!matches.length;
    this.previous.hidden=this.next.hidden=matches.length<=this.pageSize;
    if(!matches.length) {this.anchorId=null;return;}
    const visible=matches.slice(this.resultIndex,this.resultIndex+this.pageSize);
    this.anchorId=visible[0].product.id;
    visible.forEach(match=>{
      const card=this.card(match), select=card.querySelector('select'), chosen=choices.get(String(match.product.id));
      if(chosen && [...select.options].some(option=>option.value===chosen)) select.value=chosen;
      this.matchContainer.append(card);
    });
    this.previous.disabled=this.resultIndex===0;
    this.next.disabled=this.resultIndex+this.pageSize>=matches.length;
    this.range.textContent=this.copy.match_range.replace('{start}',this.resultIndex+1).replace('{end}',this.resultIndex+visible.length).replace('{total}',matches.length);
    this.measurement?.scanProducts();
  }
  element(tag, text, className) { const el=document.createElement(tag); if(text) el.textContent=text; if(className) el.className=className; return el; }
  card(match) {
    const {product,variant,variants,reasons,methodEvidence}=match;
    const card=this.element('article',null,'coffee-finder__match');
    card.dataset.productId=product.id;
    const imageFrame=this.element('div',null,'coffee-finder__product-image');
    const image=this.element('img');
    const imageUrl=new URL(product.image,location.origin);
    if(imageUrl.origin===location.origin || imageUrl.hostname.endsWith('.shopify.com') || imageUrl.hostname.endsWith('.shopifycdn.com')) image.src=imageUrl.href;
    image.alt=product.title; image.loading='lazy'; image.width=320;image.height=400;imageFrame.append(image);
    card.append(imageFrame,this.element('p',product.vendor,'coffee-finder__vendor'),this.element('h3',product.title));
    const notes=this.element('p',null,'coffee-finder__notes');
    // Descriptors are flavor impressions, not added ingredients/allergen or caffeine claims.
    const noteValue=product.notes || product.profile.notes?.[this.dataset.locale] || product.profile.notes?.en || '';
    if(noteValue) notes.append(this.element('strong',this.copy.notes+': '),document.createTextNode(noteValue));
    card.append(notes);
    const why=this.element('ul',null,'coffee-finder__reasons');
    reasons.filter(reason=>reason!=='flavor').forEach(reason=>why.append(this.element('li',this.copy['reason_'+reason])));
    if(methodEvidence.level>0) {
      const explanation=this.element('li',this.copy['method_evidence_'+methodEvidence.kind]);
      if(methodEvidence.url) {
        explanation.append(document.createTextNode(' '));
        const source=this.element('a',this.copy.method_source);source.href=methodEvidence.url;
        source.target='_blank';source.rel='noopener noreferrer';
        source.style.textDecoration='underline';source.style.textUnderlineOffset='3px';explanation.append(source);
      }
      why.append(explanation);
    }
    if(why.childElementCount) card.append(why);
    const label=this.element('div',null,'coffee-finder__field');
    const select=this.element('select'); select.setAttribute('aria-label',product.title);
    const money=new Intl.NumberFormat(this.dataset.locale,{style:'currency',currency:this.currency});
    variants.forEach(v=>{const option=this.element('option',`${v.title} · ${money.format(v.price/100)}`);option.value=v.id;select.append(option);});
    const control=this.element('coffee-finder-select',null,'coffee-finder__select');
    control.append(select,this.querySelector('[data-select-caret]').content.cloneNode(true));
    label.append(control);
    const actions=this.element('div',null,'coffee-finder__product-actions');
    const button=this.element('button',this.copy.view,'coffee-finder__primary');button.type='button';
    button.setAttribute('aria-label',`${this.copy.view} — ${product.title}`);
    button.addEventListener('click',async()=>{
      if(this.busy) return;
      const openedAt=Date.now();
      this.measurement?.productClick(product,select.value,this.rankedMatches.findIndex(m=>m.product.id===product.id)+1);
      this.savePosition();
      // Reserve the requested tab within the click gesture, before async stock validation.
      const productTab=window.open('about:blank','_blank');
      if(!productTab) {this.measurement?.error('popup_blocked','product');this.results.querySelector(':scope > [role="status"]').textContent=this.copy.error;return;}
      productTab.opener=null;
      this.openingProduct=productTab;
      const chosen=select.value;
      this.busy=true;this.previous.disabled=this.next.disabled=true;
      const controls=[...card.querySelectorAll('button,select')].map(el=>[el,el.disabled]);
      controls.forEach(([el])=>{el.disabled=true;});
      const peers=[...this.matchContainer.querySelectorAll('.coffee-finder__primary')].filter(el=>el!==button);
      peers.forEach(el=>el.setAttribute('aria-disabled','true'));
      this.results.querySelector(':scope > [role="status"]').textContent=this.copy.loading;
      let viewGeneration;
      try {
        const request=this.freshCatalog();viewGeneration=this.generation;
        const {catalog}=await request;
        const current=rankCoffees(catalog,this.answers()).find(m=>m.product.id===product.id);
        const selected=current && eligibleVariants(current.product).find(v=>v.id===chosen && (!this.answers().budget || v.price<=this.answers().budget*100));
        if(!selected) {this.measurement?.error('stock_changed','product');productTab.close();this.openingProduct=null;await this.showResults(false);this.results.querySelector(':scope > [role="status"]').textContent=this.copy.stock_changed;return;}
        const target=new URL(current.product.url,location.origin);
        if(target.origin!==location.origin || !target.pathname.includes('/products/')) throw new Error('Invalid product path');
        target.searchParams.set('variant',chosen);
        if(!productTab.closed) {productTab.location.replace(target.href);this.measurement?.product(current.product,chosen,this.rankedMatches.findIndex(m=>m.product.id===product.id)+1,Date.now()-openedAt);}
        this.results.querySelector(':scope > [role="status"]').textContent='';
      } catch(error) {
        productTab.close();
        this.measurement?.error('catalog_unavailable','product');
        if(error.cfGeneration !== undefined && error.cfGeneration !== this.generation) return;
        this.clearMatches();this.busy=false;this.results.querySelector(':scope > [role="status"]').textContent=this.copy.error;this.querySelector('[data-retry]').hidden=false;this.browse.hidden=true;
      if(!this.recovery.hidden) {this.recoveryError=true;this.querySelector('[data-recovery-status]').textContent=this.copy.error;}
      } finally {
        if(this.openingProduct===productTab) this.openingProduct=null;
        controls.forEach(([el,disabled])=>{if(el.isConnected)el.disabled=disabled;});
        peers.forEach(el=>el.removeAttribute('aria-disabled'));
        if(viewGeneration===this.generation) {
          this.busy=false;
          this.previous.disabled=this.resultIndex===0;
          this.next.disabled=this.resultIndex+this.pageSize>=this.rankedMatches.length;
        }
      }
    });
    actions.append(label,button);card.append(actions);
    return card;
  }
}
if(!customElements.get('coffee-finder')) customElements.define('coffee-finder',CoffeeFinder);
